import React, { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { TopNav } from '@/components/TopNav';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, ShieldStar, X, Key, TrendUp } from '@phosphor-icons/react';
import { currency, parseISO, formatPretty } from '@/lib/period';

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: 'user', snapshot_enabled: false });
  const [creating, setCreating] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || form.password.length < 4) {
      toast.error('Username and password (4+) required.'); return;
    }
    setCreating(true);
    try {
      await api.post('/admin/users', form);
      toast.success('User created.');
      setForm({ username: '', password: '', role: 'user', snapshot_enabled: false });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setCreating(false); }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`Delete user "${username}" and all their entries?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User deleted.');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSnapshot = async (u, next) => {
    // Optimistic update
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, snapshot_enabled: next } : x)));
    try {
      await api.put(`/admin/users/${u.id}`, { snapshot_enabled: next });
      toast.success(next ? `Weekly Snapshot on for ${u.username}` : `Weekly Snapshot off for ${u.username}`);
    } catch (err) {
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, snapshot_enabled: !next } : x)));
      toast.error(formatApiError(err));
    }
  };

  const openReset = (u) => {
    setResetTarget(u);
    setResetPwd('');
    setResetOpen(true);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPwd.length < 4) { toast.error('New password must be at least 4 characters.'); return; }
    setResetting(true);
    try {
      await api.put(`/admin/users/${resetTarget.id}`, { new_password: resetPwd });
      toast.success(`Password reset for ${resetTarget.username}`);
      setResetOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setResetting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow mb-1">USER MANAGEMENT</div>
          <h2 className="font-display text-2xl tracking-tight">{users.length} account{users.length !== 1 ? 's' : ''}</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-user-btn" className="gold-btn">
              <Plus size={16} weight="bold" className="mr-2" /> New user
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Create user account</DialogTitle>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4">
              <div>
                <Label className="label-eyebrow mb-2 block">Username</Label>
                <Input
                  data-testid="admin-new-username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="bg-black border-white/15 h-11"
                />
              </div>
              <div>
                <Label className="label-eyebrow mb-2 block">Password</Label>
                <Input
                  data-testid="admin-new-password"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-black border-white/15 h-11 font-mono"
                  placeholder="min 4 chars"
                />
              </div>
              <div>
                <Label className="label-eyebrow mb-2 block">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="admin-new-role" className="bg-black border-white/15 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User · bartender</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-4 py-3">
                <div>
                  <div className="flex items-center gap-1.5 text-sm text-white">
                    <TrendUp size={14} weight="duotone" className="text-[#D4AF37]" />
                    Weekly Snapshot
                  </div>
                  <div className="text-xs text-slate-500">Mid-period pace projection on their dashboard</div>
                </div>
                <Switch
                  data-testid="admin-new-snapshot"
                  checked={form.snapshot_enabled}
                  onCheckedChange={(v) => setForm({ ...form, snapshot_enabled: v })}
                  className="data-[state=checked]:bg-[#D4AF37]"
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="admin-new-submit" disabled={creating} className="gold-btn">
                  {creating ? 'Creating…' : 'Create account'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="card-surface !p-0 overflow-hidden">
        <div className="overflow-x-auto -mx-px">
        <Table className="min-w-[560px]">
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="label-eyebrow">Username</TableHead>
              <TableHead className="label-eyebrow">Role</TableHead>
              <TableHead className="label-eyebrow">Snapshot</TableHead>
              <TableHead className="label-eyebrow">Created</TableHead>
              <TableHead className="label-eyebrow text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-slate-500 text-center py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && users.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-slate-500 text-center py-8">No users yet.</TableCell></TableRow>
            )}
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.username}`} className="border-white/5">
                <TableCell className="font-medium text-white">{u.username}</TableCell>
                <TableCell>
                  {u.role === 'admin' ? (
                    <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/20">
                      <ShieldStar size={12} weight="duotone" className="mr-1" /> Admin
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-white/15 text-slate-300">Bartender</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid={`admin-snapshot-toggle-${u.username}`}
                      checked={!!u.snapshot_enabled}
                      onCheckedChange={(v) => toggleSnapshot(u, v)}
                      className="data-[state=checked]:bg-[#D4AF37]"
                    />
                    <span className="text-xs text-slate-500">
                      {u.snapshot_enabled ? 'On' : 'Off'}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-slate-400 text-sm">
                  {new Date(u.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      data-testid={`admin-reset-pwd-${u.username}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => openReset(u)}
                      className="text-slate-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      title="Reset password"
                    >
                      <Key size={14} weight="duotone" />
                    </Button>
                    <Button
                      data-testid={`admin-delete-user-${u.username}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteUser(u.id, u.username)}
                      className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                      title="Delete user"
                    >
                      <Trash size={14} weight="duotone" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Reset password dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Reset password{resetTarget ? ` · ${resetTarget.username}` : ''}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReset} className="space-y-4">
            <div>
              <Label className="label-eyebrow mb-2 block">New password</Label>
              <Input
                data-testid="admin-reset-password-input"
                type="text"
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                className="bg-black border-white/15 h-11 font-mono"
                placeholder="min 4 chars"
                autoFocus
              />
              <div className="text-xs text-slate-500 mt-2">
                Share this new password with {resetTarget?.username} directly. They&apos;ll be able to sign in with it immediately.
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="admin-reset-password-submit"
                disabled={resetting}
                className="gold-btn"
              >
                {resetting ? 'Saving…' : 'Reset password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RatesTab() {
  const [config, setConfig] = useState({ hr_options: [], overtime_rate: 25 });
  const [newRate, setNewRate] = useState('');
  const [otRate, setOtRate] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/config');
      setConfig(data);
      setOtRate(String(data.overtime_rate));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addRate = async (e) => {
    e.preventDefault();
    const r = Number(newRate);
    if (isNaN(r) || r <= 0) { toast.error('Enter a valid hourly rate.'); return; }
    if (config.hr_options.includes(r)) { toast.error('Rate already exists.'); return; }
    const updated = [...config.hr_options, r];
    try {
      const { data } = await api.put('/config', { hr_options: updated });
      setConfig(data);
      setNewRate('');
      toast.success(`Added $${r}/hr option.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removeRate = async (r) => {
    if (config.hr_options.length <= 1) { toast.error('At least one rate is required.'); return; }
    const updated = config.hr_options.filter((x) => x !== r);
    try {
      const { data } = await api.put('/config', { hr_options: updated });
      setConfig(data);
      toast.success(`Removed $${r}/hr option.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const saveOtRate = async () => {
    const r = Number(otRate);
    if (isNaN(r) || r <= 0) { toast.error('Enter a valid overtime rate.'); return; }
    try {
      const { data } = await api.put('/config', { overtime_rate: r });
      setConfig(data);
      toast.success('Overtime rate updated.');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="card-surface !p-6">
        <div className="label-eyebrow mb-2">HOURLY RATE OPTIONS</div>
        <h3 className="font-display text-xl mb-4">Available $/hr presets</h3>

        <div className="flex flex-wrap gap-2 mb-6">
          {config.hr_options.map((r) => (
            <div
              key={r}
              data-testid={`rate-chip-${r}`}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] font-mono text-sm"
            >
              ${r}/hr
              <button
                data-testid={`rate-remove-${r}`}
                onClick={() => removeRate(r)}
                className="hover:text-red-400 transition-colors"
                aria-label={`Remove ${r}`}
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>

        <form onSubmit={addRate} className="flex gap-2">
          <Input
            data-testid="rate-new-input"
            type="number"
            step="0.01"
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            placeholder="e.g. 18"
            className="bg-black border-white/15 h-11 font-mono"
          />
          <Button data-testid="rate-add-btn" type="submit" className="gold-btn px-5">
            <Plus size={14} weight="bold" className="mr-1" /> Add
          </Button>
        </form>
      </Card>

      <Card className="card-surface !p-6">
        <div className="label-eyebrow mb-2">OVERTIME</div>
        <h3 className="font-display text-xl mb-4">Rate per OT hour</h3>
        <div className="flex gap-2">
          <Input
            data-testid="overtime-rate-input"
            type="number"
            step="0.01"
            value={otRate}
            onChange={(e) => setOtRate(e.target.value)}
            className="bg-black border-white/15 h-11 font-mono text-lg"
          />
          <Button data-testid="overtime-save-btn" onClick={saveOtRate} className="gold-btn px-5">
            <FloppyDisk size={14} weight="duotone" className="mr-1" /> Save
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Currently {currency(config.overtime_rate)}/hr — applies to any shift with overtime toggled on.
        </p>
      </Card>
    </div>
  );
}

function LedgerTab() {
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([
          api.get('/admin/summary'),
          api.get('/admin/entries'),
        ]);
        setSummary(s.data.summary || []);
        setEntries(e.data.entries || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-slate-400">Loading ledger…</div>;

  const grandTotal = summary.reduce((a, b) => a + b.gross, 0);

  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="card-surface !p-5">
          <div className="label-eyebrow">Active Users</div>
          <div className="font-mono text-2xl mt-2 text-white">{summary.length}</div>
        </Card>
        <Card className="card-surface !p-5">
          <div className="label-eyebrow">Total Shifts</div>
          <div className="font-mono text-2xl mt-2 text-white">
            {summary.reduce((a, b) => a + b.shifts, 0)}
          </div>
        </Card>
        <Card className="card-surface !p-5 border-[#D4AF37]/30 gold-glow">
          <div className="label-eyebrow text-[#D4AF37]">Grand Total</div>
          <div className="font-mono text-2xl mt-2 text-[#D4AF37]">{currency(grandTotal)}</div>
        </Card>
      </div>

      <Card className="card-surface !p-0 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-white/5">
          <h3 className="font-display text-lg">By bartender</h3>
        </div>
        <div className="overflow-x-auto">
        <Table className="min-w-[700px]">
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="label-eyebrow">Bartender</TableHead>
              <TableHead className="label-eyebrow">Shifts</TableHead>
              <TableHead className="label-eyebrow">Regular</TableHead>
              <TableHead className="label-eyebrow">Overtime</TableHead>
              <TableHead className="label-eyebrow">Tips</TableHead>
              <TableHead className="label-eyebrow">Cash tips</TableHead>
              <TableHead className="label-eyebrow text-right">Gross</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-slate-500 text-center py-8">No entries yet.</TableCell></TableRow>
            )}
            {summary.map((s) => (
              <TableRow key={s.user_id} className="border-white/5">
                <TableCell className="font-medium text-white">{s.username}</TableCell>
                <TableCell className="font-mono text-slate-300">{s.shifts}</TableCell>
                <TableCell className="font-mono text-slate-300">{currency(s.regular_pay)}</TableCell>
                <TableCell className="font-mono text-[#F97316]">{currency(s.overtime_pay)}</TableCell>
                <TableCell className="font-mono text-[#10B981]">{currency(s.tips)}</TableCell>
                <TableCell className="font-mono text-slate-400">{currency(s.cash_tips || 0)}</TableCell>
                <TableCell className="font-mono text-[#D4AF37] text-right">{currency(s.gross)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </Card>

      <Card className="card-surface !p-0 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-white/5">
          <h3 className="font-display text-lg">Recent entries · all bartenders</h3>
        </div>
        <div className="max-h-[480px] overflow-auto">
          <Table className="min-w-[640px]">
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent sticky top-0 bg-[#121212]">
                <TableHead className="label-eyebrow">Date</TableHead>
                <TableHead className="label-eyebrow">User</TableHead>
                <TableHead className="label-eyebrow">Hrs</TableHead>
                <TableHead className="label-eyebrow">Rate</TableHead>
                <TableHead className="label-eyebrow">OT Hrs</TableHead>
                <TableHead className="label-eyebrow">Tips</TableHead>
                <TableHead className="label-eyebrow text-right">Cash tips</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.slice(0, 200).map((e) => (
                <TableRow key={e.id} className="border-white/5">
                  <TableCell className="font-mono text-slate-300">{formatPretty(parseISO(e.date))}</TableCell>
                  <TableCell className="text-white">{e.username}</TableCell>
                  <TableCell className="font-mono text-slate-300">{e.hours}</TableCell>
                  <TableCell className="font-mono text-slate-300">${e.rate}</TableCell>
                  <TableCell className="font-mono text-[#F97316]">{e.overtime ? e.overtime_hours : '—'}</TableCell>
                  <TableCell className="font-mono text-[#10B981]">{currency(e.tips || 0)}</TableCell>
                  <TableCell className="font-mono text-slate-400 text-right">{currency(e.cash_tips || 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  return (
    <div className="min-h-screen relative">
      <div className="grain-overlay" />
      <TopNav />

      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative z-10"
        style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mb-6 sm:mb-8">
          <div className="label-eyebrow mb-2">ADMIN</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">Control room</h1>
          <p className="text-slate-400 mt-1 text-sm sm:text-base">Manage bartenders, rates, and view the full ledger.</p>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList data-testid="admin-tabs" className="bg-[#121212] border border-white/10">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Users</TabsTrigger>
            <TabsTrigger value="rates" data-testid="admin-tab-rates" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Rates</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="admin-tab-ledger" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Ledger</TabsTrigger>
          </TabsList>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="rates"><RatesTab /></TabsContent>
          <TabsContent value="ledger"><LedgerTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
